import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { Gear, Key, EnvelopeSimple, Lock } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsPage() {
  const { user, login } = useAuth();
  const [newEmail, setNewEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwSaving, setPwSaving] = useState(false);

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    if (!newEmail || !emailPassword) { toast.error("Fill all fields"); return; }
    setEmailSaving(true);
    try {
      const { data } = await axios.post(`${API}/auth/change-email`, { new_email: newEmail, password: emailPassword }, { withCredentials: true });
      if (data.token) localStorage.setItem("cms_token", data.token);
      toast.success(`Email changed to ${data.email}`);
      setNewEmail("");
      setEmailPassword("");
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change email");
    } finally { setEmailSaving(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword) { toast.error("Fill all fields"); return; }
    if (newPassword !== confirmPassword) { toast.error("Passwords don't match"); return; }
    if (newPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setPwSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: currentPassword, new_password: newPassword }, { withCredentials: true });
      toast.success("Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally { setPwSaving(false); }
  };

  return (
    <div data-testid="settings-page" className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl sm:text-3xl font-black tracking-tighter text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>Settings</h1>
        <p className="text-sm text-[#A1A1AA] mt-1">Account and system configuration</p>
      </div>

      {/* Change Email */}
      <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm">
        <div className="px-5 py-4 border-b border-[#2A2A2E] flex items-center gap-2">
          <EnvelopeSimple size={16} className="text-[#4361EE]" />
          <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#A1A1AA]">Change Email</h3>
        </div>
        <form onSubmit={handleChangeEmail} className="p-5 space-y-3">
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">Current Email</label>
            <Input value={user?.email || ""} disabled className="bg-[#0A0A0C] border-[#2A2A2E] text-[#555] rounded-sm h-10" />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">New Email</label>
            <Input data-testid="new-email-input" type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="new@email.com" className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10" required />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">Current Password (confirm)</label>
            <Input data-testid="email-password-input" type="password" value={emailPassword} onChange={e => setEmailPassword(e.target.value)} className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10" required />
          </div>
          <Button data-testid="change-email-btn" type="submit" disabled={emailSaving} className="bg-[#4361EE] hover:bg-[#3651DE] text-white rounded-sm px-6 text-sm font-semibold">
            {emailSaving ? "Updating..." : "Update Email"}
          </Button>
        </form>
      </div>

      {/* Change Password */}
      <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm">
        <div className="px-5 py-4 border-b border-[#2A2A2E] flex items-center gap-2">
          <Lock size={16} className="text-[#E53935]" />
          <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#A1A1AA]">Change Password</h3>
        </div>
        <form onSubmit={handleChangePassword} className="p-5 space-y-3">
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">Current Password</label>
            <Input data-testid="current-password-input" type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10" required />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">New Password</label>
            <Input data-testid="new-password-input" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min 6 characters" className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10" required />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">Confirm New Password</label>
            <Input data-testid="confirm-password-input" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10" required />
          </div>
          <Button data-testid="change-password-btn" type="submit" disabled={pwSaving} className="bg-[#E53935] hover:bg-[#C62828] text-white rounded-sm px-6 text-sm font-semibold">
            {pwSaving ? "Updating..." : "Update Password"}
          </Button>
        </form>
      </div>

      {/* AI Integration */}
      <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm">
        <div className="px-5 py-4 border-b border-[#2A2A2E] flex items-center gap-2">
          <Key size={16} className="text-amber-400" />
          <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#A1A1AA]">AI Integration</h3>
        </div>
        <div className="p-5">
          <p className="text-xs text-[#555]">Mistral AI is configured server-side via .env file. Contact your administrator to update the API key.</p>
        </div>
      </div>
    </div>
  );
}
